/**
 * CustomCodesAndContextStep — codebook + context step in the analysis
 * stepper.
 *
 * Phase 4 of the document-unification work: the previous version
 * accepted unstructured PDFs/DOCX/TXT and let the worker LLM extract
 * codes. The new version is structured-only:
 *
 *   1. Codebook — two options:
 *      a) Let AI find the codes (generate path, default)
 *      b) Upload my codebook (.csv / .xlsx with `codes` + `definition`
 *         columns) → backend parses → confirmation table shown here →
 *         confirmed rows are sent inline as `custom_codes` on the run.
 *
 *   2. Custom context (optional) — single textarea that becomes
 *      `custom_context` on the run. Adds research framing for the LLM.
 *
 * No paste path, no LLM file-parsing. The user sees exactly what the
 * worker will see; re-upload to correct.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Link as MuiLink,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { parseCodebook } from '@services/codebook';

const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Build a .csv blob the user can download as a starter template.
 * Inlined so we don't need a static asset round-trip — the file is
 * three lines, generating it client-side keeps the bundle smaller and
 * avoids a CDN dependency.
 */
function buildTemplateBlob() {
  const text =
    'codes,definition\n' +
    'change,"any kind of meaningful change in the participant\'s situation"\n' +
    'freedom,"the feeling or experience of being free"\n';
  return new Blob([text], { type: 'text/csv;charset=utf-8' });
}

function triggerTemplateDownload() {
  const blob = buildTemplateBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'codebook-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function CustomCodesAndContextStep({
  studyId,
  customCodes,
  setCustomCodes,
  customContext,
  setCustomContext,
  pauseForReview = false,
  setPauseForReview,
  setActiveStep,
  onContinue,
}) {
  // Default to 'skip' (generate path) — keeps the lowest-friction
  // option in front. 'upload' is implied the moment the user has any
  // confirmed codes (e.g. revisiting the step after going back).
  const initialMode = customCodes && customCodes.length > 0 ? 'upload' : 'skip';
  const [mode, setMode] = useState(initialMode);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const parseMutation = useMutation({
    mutationFn: ({ file }) => parseCodebook({ studyId, file }),
    onSuccess: (data, vars) => {
      setCustomCodes(data.rows || []);
      setWarnings(data.warnings || []);
      setUploadedFile({ name: vars.file.name, size: vars.file.size });
      setUploadError(null);
    },
    onError: (err) => {
      setCustomCodes([]);
      setWarnings([]);
      setUploadedFile(null);
      setUploadError(err?.response?.data?.detail || err?.message || 'Upload failed');
    },
  });

  const handleModeChange = (next) => {
    setMode(next);
    if (next === 'skip') {
      setCustomCodes([]);
      setUploadedFile(null);
      setWarnings([]);
      setUploadError(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > MAX_BYTES) {
      setUploadError(`File exceeds ${MAX_BYTES / (1024 * 1024)} MB limit.`);
      return;
    }
    parseMutation.mutate({ file });
  };

  const handleRemoveUpload = () => {
    setCustomCodes([]);
    setUploadedFile(null);
    setWarnings([]);
    setUploadError(null);
  };

  const parsedCount = customCodes?.length || 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h6">Codes & context</Typography>
          <Typography variant="body2" color="text.secondary">
            Both are optional — skip either and AI will figure it out.
          </Typography>
        </Box>
      </Stack>

      {/* --- Codebook card --- */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Your codebook
            </Typography>
            <Button
              size="small"
              startIcon={<DownloadOutlinedIcon />}
              onClick={triggerTemplateDownload}
            >
              Download template
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Bring your own codes to apply, or let AI find them for you.
          </Typography>

          <RadioGroup
            value={mode}
            onChange={(e) => handleModeChange(e.target.value)}
            sx={{ mb: 2 }}
          >
            <FormControlLabel
              value="skip"
              control={<Radio />}
              label={
                <Typography variant="body2" fontWeight={500}>
                  Let AI find the codes
                </Typography>
              }
            />
            <FormControlLabel
              value="upload"
              control={<Radio />}
              label={
                <Typography variant="body2" fontWeight={500}>
                  Upload my codebook (.csv / .xlsx)
                </Typography>
              }
            />
          </RadioGroup>

          {mode === 'upload' ? (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                File must have two columns named{' '}
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  codes
                </Box>{' '}
                and{' '}
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  definition
                </Box>
                .{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={triggerTemplateDownload}
                >
                  Need a template?
                </MuiLink>
              </Typography>

              {uploadedFile ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <DescriptionOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {uploadedFile.name}
                  </Typography>
                  <Chip
                    size="small"
                    color="primary"
                    label={`${parsedCount} ${parsedCount === 1 ? 'code' : 'codes'}`}
                  />
                  <IconButton size="small" onClick={handleRemoveUpload}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadOutlinedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={parseMutation.isPending}
                >
                  {parseMutation.isPending ? 'Parsing…' : 'Choose a file'}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              {uploadError ? (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUploadError(null)}>
                  {uploadError}
                </Alert>
              ) : null}

              {warnings.length > 0 ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Stack spacing={0.5}>
                    {warnings.map((w, idx) => (
                      <Typography key={idx} variant="caption">
                        {w}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              ) : null}

              {parsedCount > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Review your codes — re-upload to make changes.
                  </Typography>
                  <TableContainer
                    sx={{
                      maxHeight: 320,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: '30%' }}>Code</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Definition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customCodes.map((row, idx) => (
                          <TableRow key={`${row.name}-${idx}`}>
                            <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                              {row.name}
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {row.definition || (
                                <Typography variant="caption" color="text.disabled">
                                  (no definition)
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* --- Custom context card --- */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Anything else AI should know?{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              (optional)
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Give AI a bit of background to make the analysis richer.
          </Typography>
          <TextField
            value={customContext || ''}
            onChange={(e) => setCustomContext(e.target.value)}
            placeholder="Write anything you want AI to keep in mind…"
            multiline
            minRows={4}
            fullWidth
            inputProps={{ maxLength: 4000 }}
          />
        </CardContent>
      </Card>

      {/* --- Advanced: pause for review card ---
          Independent of the codebook choice above so a researcher with
          their own codebook can still review the AI's per-paragraph
          assignments before themes are built. */}
      {setPauseForReview ? (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="overline"
              color="primary"
              sx={{ fontWeight: 700, letterSpacing: 1, display: 'block', mb: 0.5 }}
            >
              Advanced
            </Typography>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Review and edit codes before themes
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              By default, the analysis runs end-to-end. Turn this on to pause after open coding so
              you can refine the codebook — rename, unassign, or delete codes, and add new ones —
              before categories and themes are generated. A{' '}
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 0.75,
                  py: 0.1,
                  fontWeight: 600,
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  bgcolor: 'background.paper',
                  mx: 0.25,
                }}
              >
                Continue Analysis
              </Box>{' '}
              button appears on the results page once coding is complete.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={!!pauseForReview}
                  onChange={(e) => setPauseForReview(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Pause after open coding so I can review
                </Typography>
              }
            />
          </CardContent>
        </Card>
      ) : null}

      <Divider sx={{ my: 3 }} />
      <Stack direction="row" justifyContent="space-between">
        <Button variant="outlined" onClick={() => setActiveStep(1)}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={mode === 'upload' && parseMutation.isPending}
        >
          Continue
        </Button>
      </Stack>
    </Box>
  );
}

export default CustomCodesAndContextStep;
